import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';
import { useHeroManager } from '../../../hooks/useHeroManager';
import { HERO_TESTIMONIALS } from '../../../mocks/heroTestimonials';

export default function Testimonials() {
  const { getActiveHero } = useHeroManager();
  const activeHero = getActiveHero();

  const activeId = activeHero?.id ?? 'hero-1-original';
  const testimonials = HERO_TESTIMONIALS[activeId] ?? HERO_TESTIMONIALS['hero-1-original'];
  const serviceLabel = testimonials[0]?.service ?? 'Assessoria Técnica';

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggerAnimation(testimonials.length);
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section className="py-16 sm:py-20 lg:py-28 bg-white" aria-labelledby="testimonials-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-strategic-green-100 px-4 py-2 rounded-full mb-3 sm:mb-4" aria-hidden="true">
            <i className="ri-chat-quote-line text-strategic-green-700 text-base"></i>
            <span className="text-strategic-green-800 font-bold text-xs sm:text-sm uppercase tracking-wide">Depoimentos</span>
          </div>

          <div className="flex justify-center mb-4 sm:mb-6">
            <span className="inline-flex items-center gap-2 bg-action-orange-50 border border-action-orange-300 text-action-orange-800 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full">
              <i className="ri-shield-check-line text-action-orange-600" aria-hidden="true"></i>
              {serviceLabel}
            </span>
          </div>

          <h2
            id="testimonials-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-900 mb-4 sm:mb-6 px-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            O Que Nossos Clientes{' '}
            <span className="text-strategic-green-600">Dizem</span>
          </h2>
          <p className="text-base sm:text-lg text-petrol-800 max-w-3xl mx-auto leading-relaxed px-4">
            Resultados reais de empresas que transformaram sua gestão de seguros com a IBX Brasil.
          </p>
        </div>

        <div ref={gridRef as React.RefObject<HTMLDivElement>} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12">
          {testimonials.map((testimonial, index) => (
            <article
              key={`${activeId}-${index}`}
              className={`bg-gradient-to-br from-petrol-50 to-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl border border-petrol-100 transition-all duration-700 ease-out ${visibleItems[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              <div className="flex items-start justify-between mb-4 sm:mb-5">
                <div className="flex gap-1" aria-label={`Avaliação: ${testimonial.rating} de 5 estrelas`}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-action-orange-500 text-base" aria-hidden="true"></i>
                  ))}
                </div>
                <i className="ri-double-quotes-r text-3xl text-petrol-200" aria-hidden="true"></i>
              </div>

              <p className="text-sm sm:text-base text-petrol-800 leading-relaxed mb-6 sm:mb-8 italic">
                "{testimonial.text}"
              </p>

              <div className="flex items-center gap-3 sm:gap-4 pt-4 border-t border-petrol-100">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden flex-shrink-0 bg-petrol-100">
                  <img
                    src={testimonial.image}
                    alt={`Foto de ${testimonial.name}`}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                    width="56"
                    height="56"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-petrol-900 text-sm sm:text-base truncate">{testimonial.name}</div>
                  <div className="text-xs sm:text-sm text-petrol-700 truncate">{testimonial.role}</div>
                  <div className="text-xs text-petrol-600 truncate">{testimonial.company}</div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`text-center transition-all duration-700 ease-out ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link
            to="/depoimentos"
            className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-petrol-900 text-white px-6 sm:px-8 py-4 rounded-xl text-base sm:text-lg font-bold hover:bg-petrol-800 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
            aria-label="Ver mais depoimentos de clientes da IBX Brasil"
          >
            <i className="ri-chat-3-line text-xl" aria-hidden="true"></i>
            Ver Mais Depoimentos
          </Link>
        </div>

      </div>
    </section>
  );
}
